export interface LapHistoryEntry {
  total: number;
  sectors: [number, number, number];
}

export interface TimingState {
  running: boolean;
  bestLap: number | null;
  bestSectors: [number | null, number | null, number | null];
  currentLapStart: number;
  currentSectorStart: number;
  currentSectorIndex: 0 | 1 | 2;
  currentSectorTimes: [number, number, number];
  lapHistory: LapHistoryEntry[];
}

export interface SectorClosed {
  sector: 0 | 1 | 2;
  time: number;
  isPersonalBest: boolean;
  /** sectorTime - previous best for this sector. null if no prior best. */
  delta: number | null;
}

export interface LapClosed {
  total: number;
  sectors: [number, number, number];
  isPersonalBest: boolean;
}

export interface LapEvent {
  sectorClosed?: SectorClosed;
  lapClosed?: LapClosed;
}

export interface NotifyCheckpointOptions {
  /** False for practice/assisted laps that should show timing but not write PBs. */
  updateBests?: boolean;
}

const DEFAULT_TRACK_ID = 'twin-suns-autodrome';
const STORAGE_PREFIX = 'tst:bests:';

export interface LapTimerOptions {
  trackId?: string;
  storageScope?: string | null;
}

interface PersistedBests {
  bestLap: number | null;
  bestSectors: [number | null, number | null, number | null];
}

export class LapTimer {
  state: TimingState;
  private readonly trackId: string;
  private storageScope: string | null;

  constructor(options: LapTimerOptions = {}) {
    this.trackId = options.trackId ?? DEFAULT_TRACK_ID;
    this.storageScope = options.storageScope ?? null;
    const persisted = loadPersisted(this.storageKey(), this.legacyStorageKey(), this.storageScope);
    this.state = {
      running: false,
      bestLap: persisted?.bestLap ?? null,
      bestSectors: persisted?.bestSectors ?? [null, null, null],
      currentLapStart: 0,
      currentSectorStart: 0,
      currentSectorIndex: 0,
      currentSectorTimes: [0, 0, 0],
      lapHistory: [],
    };
  }

  /** Begin timing on first real driving input. */
  start(now: number): void {
    this.state.running = true;
    this.state.currentLapStart = now;
    this.state.currentSectorStart = now;
    this.state.currentSectorIndex = 0;
    this.state.currentSectorTimes = [0, 0, 0];
  }

  reset(now: number): void {
    this.state.running = false;
    this.state.currentLapStart = now;
    this.state.currentSectorStart = now;
    this.state.currentSectorIndex = 0;
    this.state.currentSectorTimes = [0, 0, 0];
  }

  setStorageScope(storageScope: string | null): void {
    if (storageScope === this.storageScope) return;
    this.storageScope = storageScope;
    if (this.state.running) return;
    const persisted = loadPersisted(this.storageKey(), this.legacyStorageKey(), this.storageScope);
    this.state.bestLap = persisted?.bestLap ?? null;
    this.state.bestSectors = persisted?.bestSectors ?? [null, null, null];
  }

  setPersonalBest(total: number, sectors: [number, number, number]): void {
    if (!Number.isFinite(total) || total <= 0) return;
    if (sectors.some((sector) => !Number.isFinite(sector) || sector <= 0)) return;
    this.state.bestLap = total;
    this.state.bestSectors = [...sectors];
    this.persistBests();
  }

  /** Pass when a checkpoint is crossed. `sectorIndex` is the sector this CP closes. */
  notifyCheckpoint(
    sectorIndex: 0 | 1 | 2,
    now: number,
    options: NotifyCheckpointOptions = {},
  ): LapEvent {
    const ev: LapEvent = {};
    const updateBests = options.updateBests ?? true;
    if (!this.state.running) {
      return ev;
    }
    if (sectorIndex !== this.state.currentSectorIndex) {
      return ev;
    }

    const sectorTime = now - this.state.currentSectorStart;
    this.state.currentSectorTimes[sectorIndex] = sectorTime;

    const prevBest = this.state.bestSectors[sectorIndex];
    const sectorPB = updateBests && (prevBest === null || sectorTime < prevBest);
    ev.sectorClosed = {
      sector: sectorIndex,
      time: sectorTime,
      isPersonalBest: sectorPB,
      delta: prevBest === null ? null : sectorTime - prevBest,
    };

    if (sectorIndex === 2) {
      const lapTime = now - this.state.currentLapStart;
      const lapSectors: [number, number, number] = [
        this.state.currentSectorTimes[0],
        this.state.currentSectorTimes[1],
        this.state.currentSectorTimes[2],
      ];
      const lapPB = updateBests && (this.state.bestLap === null || lapTime < this.state.bestLap);
      if (lapPB) {
        this.state.bestLap = lapTime;
        this.state.bestSectors = [...lapSectors];
        this.persistBests();
      }
      this.state.lapHistory.push({ total: lapTime, sectors: lapSectors });
      this.state.currentLapStart = now;
      this.state.currentSectorStart = now;
      this.state.currentSectorIndex = 0;
      this.state.currentSectorTimes = [0, 0, 0];
      ev.lapClosed = { total: lapTime, sectors: lapSectors, isPersonalBest: lapPB };
    } else {
      this.state.currentSectorIndex = (sectorIndex + 1) as 0 | 1 | 2;
      this.state.currentSectorStart = now;
    }

    return ev;
  }

  private storageKey(): string {
    return bestsStorageKey(this.trackId, this.storageScope);
  }

  private legacyStorageKey(): string {
    return bestsStorageKey(this.trackId, null);
  }

  private persistBests(): void {
    persistBests(this.storageKey(), {
      bestLap: this.state.bestLap,
      bestSectors: this.state.bestSectors,
    });
  }
}

export function bestsStorageKey(trackId: string, storageScope: string | null = null): string {
  return storageScope
    ? `${STORAGE_PREFIX}${trackId}:${storageScope}`
    : `${STORAGE_PREFIX}${trackId}`;
}

function loadPersisted(
  storageKey: string,
  legacyStorageKey: string,
  storageScope: string | null,
): PersistedBests | null {
  const scoped = readPersisted(storageKey);
  if (scoped) return scoped;
  if (!storageScope?.startsWith('device:') || storageKey === legacyStorageKey) return null;
  const legacy = readPersisted(legacyStorageKey);
  if (legacy) persistBests(storageKey, legacy);
  return legacy;
}

function readPersisted(storageKey: string): PersistedBests | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (typeof data !== 'object' || data === null) return null;
    const bestLap = typeof data.bestLap === 'number' ? data.bestLap : null;
    const bs = data.bestSectors;
    const bestSectors: [number | null, number | null, number | null] =
      Array.isArray(bs) && bs.length === 3
        ? [
            typeof bs[0] === 'number' ? bs[0] : null,
            typeof bs[1] === 'number' ? bs[1] : null,
            typeof bs[2] === 'number' ? bs[2] : null,
          ]
        : [null, null, null];
    if (bestLap === null) return { bestLap, bestSectors: [null, null, null] };
    return { bestLap, bestSectors: bestSectorsMatchLap(bestLap, bestSectors) ? bestSectors : [null, null, null] };
  } catch {
    return null;
  }
}

function persistBests(storageKey: string, b: PersistedBests): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(b));
  } catch {
    // ignore quota / disabled storage
  }
}

function bestSectorsMatchLap(
  bestLap: number,
  bestSectors: [number | null, number | null, number | null],
): boolean {
  if (!Number.isFinite(bestLap)) return false;
  if (bestSectors.some((sector) => sector === null || !Number.isFinite(sector))) return false;
  const total = (bestSectors as [number, number, number]).reduce((sum, sector) => sum + sector, 0);
  return Math.abs(total - bestLap) <= 5;
}

export function formatTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '--:--.---';
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = Math.floor(ms % 1000);
  return (
    String(minutes).padStart(2, '0') +
    ':' +
    String(seconds).padStart(2, '0') +
    '.' +
    String(millis).padStart(3, '0')
  );
}

export function formatSectorTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '--.---';
  const seconds = Math.floor(ms / 1000);
  const millis = Math.floor(ms % 1000);
  return String(seconds).padStart(2, '0') + '.' + String(millis).padStart(3, '0');
}
